import { useState } from 'react';
import { Check, Copy, ExternalLink, MapPin, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNow } from '@/hooks/useNow';
import { useToast } from '@/hooks/useToast';
import {
  formatDD,
  formatDDM,
  formatDMS,
  formatUtm,
  geohash,
  maidenhead,
  mgrsRef,
  plusCode,
  type GeoPoint,
} from '@/lib/geo';

interface LocationPanelProps {
  location: GeoPoint;
  placeName: string | null | undefined;
  placeLoading: boolean;
  timezone?: string;
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="group flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-accent/60">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate text-right font-mono text-xs" title={value}>
        {value}
      </span>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        className="shrink-0 rounded p-1 text-muted-foreground opacity-60 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {
            // clipboard unavailable
          }
        }}
      >
        {copied ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

function LocalTime({ timezone }: { timezone?: string }) {
  const now = useNow(1000);
  if (!timezone) return <span className="font-mono text-xs">—</span>;
  let text: string;
  try {
    text = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(now);
  } catch {
    text = now.toLocaleString();
  }
  return (
    <span className="font-mono text-xs" title={timezone}>
      {text}
    </span>
  );
}

export function LocationPanel({ location, placeName, placeLoading, timezone }: LocationPanelProps) {
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const shareOnNostr = () => {
    const gh = geohash(location, 8);
    const lines = [
      placeName ? `Location: ${placeName}` : 'Location',
      `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}`,
      `Geohash: ${gh}`,
      ``,
      `Shared from SkyScope — live sky & space tracker`,
    ];
    publish(
      {
        kind: 1,
        content: lines.join('\n'),
        tags: [
          ['g', gh],
          ['t', 'geohash'],
          ['t', 'skyscope'],
        ],
      },
      {
        onSuccess: () => toast({ title: 'Location shared to Nostr' }),
        onError: () =>
          toast({ title: 'Failed to share', description: 'Could not publish the note.', variant: 'destructive' }),
      },
    );
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="space-y-1 p-3">
          <div className="flex items-start gap-2 px-2 pb-2 pt-1">
            <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
            {placeLoading ? (
              <Skeleton className="h-4 w-3/4" />
            ) : (
              <span className="text-sm font-medium leading-snug">
                {placeName ?? 'Open waters'}
              </span>
            )}
          </div>

          <CopyRow label="Lat / Lon" value={formatDD(location)} />
          <CopyRow label="DMS" value={formatDMS(location)} />
          <CopyRow label="DDM" value={formatDDM(location)} />
          <CopyRow label="Geohash" value={geohash(location, 9)} />
          <CopyRow label="Maidenhead" value={maidenhead(location)} />
          <CopyRow label="Plus Code" value={plusCode(location)} />
          <CopyRow label="MGRS" value={mgrsRef(location)} />
          <CopyRow label="UTM" value={formatUtm(location)} />

          <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">Local time</span>
            <LocalTime timezone={timezone} />
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          asChild
        >
          <a
            href={`https://www.openstreetmap.org/?mlat=${location.lat.toFixed(5)}&mlon=${location.lon.toFixed(5)}#map=11/${location.lat.toFixed(5)}/${location.lon.toFixed(5)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="size-3.5" />
            Open in OSM
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={shareOnNostr}
          disabled={!user || isPending}
          title={user ? 'Share this location as a Nostr note' : 'Log in to share on Nostr'}
        >
          <Share2 className="size-3.5" />
          {isPending ? 'Sharing…' : 'Share on Nostr'}
        </Button>
      </div>

      <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
        Tip: click anywhere on the map to move the observer crosshair. All geocodes, the sky view,
        satellite look angles and the weather update for that point.
      </p>
    </div>
  );
}
